/**
 * Reports Page
 * Generate and manage analytical reports
 */
// @ts-nocheck

import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  VStack,
  SimpleGrid,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  FormControl,
  FormLabel,
  Checkbox,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import {
  MdSearch,
  MdRefresh,
  MdMoreVert,
  MdDownload,
  MdAdd,
  MdSchedule,
  MdPlayArrow,
  MdDelete,
  MdEdit,
  MdAssessment,
  MdPeople,
  MdAttachMoney,
  MdBusiness,
  MdSupport,
} from 'react-icons/md';
import { Report, ReportTemplate, ReportStats } from '../types/report';
import { reportService } from '../services/reportService';

export function ReportsPage() {
  const toast = useToast();
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  
  const [reports, setReports] = useState<Report[]>([]);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [stats, setStats] = useState<ReportStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    loadReports();
  }, [filterType, filterStatus]);

  const loadReports = async () => {
    try {
      setLoading(true);
      
      // Fetch reports from Firestore via Cloud Functions
      const [reportsResponse, templatesResponse, statsResponse] = await Promise.all([
        reportService.getReports({ type: filterType, status: filterStatus }),
        reportService.getTemplates(),
        reportService.getReportStats(),
      ]);
      
      setReports(reportsResponse.reports || []);
      setTemplates(templatesResponse.templates || []);
      setStats(statsResponse.stats || null);
    } catch (error: any) {
      console.error('Error loading reports:', error);
      toast({
        title: 'Error loading reports',
        description: error.message || 'Please try again later',
        status: 'error',
        duration: 5000,
      });
      // Set empty arrays on error
      setReports([]);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (reportId: string) => {
    try {
      setGenerating(reportId);
      
      await reportService.generateReport(reportId);
      
      toast({
        title: 'Report generated',
        description: 'Your report is ready for download',
        status: 'success',
        duration: 3000,
      });
      
      loadReports();
    } catch (error: any) {
      toast({
        title: 'Error generating report',
        description: error.message || 'Please try again',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setGenerating(null);
    }
  };

  const deleteReport = async (reportId: string) => {
    if (!confirm('Are you sure you want to delete this report?')) return;
    
    try {
      await reportService.deleteReport(reportId);
      
      toast({
        title: 'Report deleted',
        status: 'success',
        duration: 3000,
      });
      
      loadReports();
    } catch (error: any) {
      toast({
        title: 'Error deleting report',
        description: error.message || 'Please try again',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const downloadReport = (report: Report) => {
    if (report.dataUrl) {
      window.open(report.dataUrl, '_blank');
    } else {
      toast({
        title: 'Report not ready',
        description: 'Please generate the report first',
        status: 'warning',
        duration: 3000,
      });
    }
  };

  const getTypeColor = (type: Report['type']) => {
    switch (type) {
      case 'landlord_activity': return 'blue';
      case 'property_performance': return 'purple';
      case 'financial': return 'green';
      case 'support_metrics': return 'orange';
      case 'custom': return 'gray';
      default: return 'gray';
    }
  };

  const getTypeIcon = (type: Report['type']) => {
    switch (type) {
      case 'landlord_activity': return MdPeople;
      case 'property_performance': return MdBusiness;
      case 'financial': return MdAttachMoney;
      case 'support_metrics': return MdSupport;
      default: return MdAssessment;
    }
  };

  const getStatusColor = (status: Report['status']) => {
    switch (status) {
      case 'active': return 'green';
      case 'draft': return 'gray';
      case 'paused': return 'yellow';
      case 'archived': return 'red';
      default: return 'gray';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = 
      report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || report.type === filterType;
    const matchesStatus = filterStatus === 'all' || report.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  return (
    <Box>
      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <VStack align="start" spacing={0}>
          <Heading size="lg">Reports & Analytics</Heading>
          <Text color="gray.600" fontSize="sm">
            Generate and manage analytical reports
          </Text>
        </VStack>
        <HStack>
          <Button
            leftIcon={<MdRefresh />}
            onClick={loadReports}
            variant="outline"
            isLoading={loading}
          >
            Refresh
          </Button>
          <Button colorScheme="blue" leftIcon={<MdAdd />} onClick={onCreateOpen}>
            Create Report
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      {stats && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Total Reports</StatLabel>
                <StatNumber>{stats.total}</StatNumber>
                <StatHelpText>{stats.generatedThisMonth} generated this month</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Scheduled Reports</StatLabel>
                <StatNumber>{stats.scheduledReports}</StatNumber>
                <StatHelpText>Automated reports</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Completed</StatLabel>
                <StatNumber color="green.500">{stats.byStatus?.completed || 0}</StatNumber>
                <StatHelpText>Ready for download</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Failed</StatLabel>
                <StatNumber color="red.500">{stats.byStatus?.failed || 0}</StatNumber>
                <StatHelpText>Need attention</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Report Templates */}
      <Card bg={cardBg} borderColor={borderColor} mb={6}>
        <CardHeader>
          <Heading size="md">Quick Reports</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            {templates.map((template) => (
              <Card key={template.id} variant="outline" _hover={{ shadow: 'md' }}>
                <CardBody>
                  <VStack align="start" spacing={3}>
                    <HStack>
                      <Icon 
                        as={getTypeIcon(template.type as Report['type'])} 
                        boxSize={6} 
                        color={`${getTypeColor(template.type as Report['type'])}.500`}
                      />
                      <Badge colorScheme={getTypeColor(template.type as Report['type'])}>
                        {template.type.replace('_', ' ')}
                      </Badge>
                    </HStack>
                    <Box>
                      <Text fontWeight="bold" mb={1}>{template.name}</Text>
                      <Text fontSize="sm" color="gray.600" noOfLines={2}>
                        {template.description}
                      </Text>
                    </Box>
                    <Button 
                      size="sm" 
                      leftIcon={<MdPlayArrow />}
                      colorScheme="blue"
                      variant="ghost"
                      w="full"
                      onClick={() => generateReport(template.id)}
                      isLoading={generating === template.id}
                    >
                      Generate Now
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Filters */}
      <Card bg={cardBg} borderColor={borderColor} mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <InputGroup>
              <InputLeftElement>
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="landlord_activity">Landlord Activity</option>
              <option value="property_performance">Property Performance</option>
              <option value="financial">Financial</option>
              <option value="support_metrics">Support Metrics</option>
              <option value="custom">Custom</option>
            </Select>

            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </Select>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Reports Table */}
      <Card bg={cardBg} borderColor={borderColor}>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md">Saved Reports</Heading>
            <Text fontSize="sm" color="gray.600">
              {filteredReports.length} reports
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Flex justify="center" align="center" h="200px">
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : filteredReports.length === 0 ? (
            <Flex justify="center" align="center" h="200px" direction="column">
              <Icon as={MdAssessment} boxSize={12} color="gray.400" mb={2} />
              <Text color="gray.600">No reports found</Text>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Report Name</Th>
                    <Th>Type</Th>
                    <Th>Schedule</Th>
                    <Th>Last Run</Th>
                    <Th>Next Run</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredReports.map((report) => (
                    <Tr key={report.id} _hover={{ bg: hoverBg }}>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold">{report.name}</Text>
                          {report.description && (
                            <Text fontSize="sm" color="gray.600" noOfLines={1}>
                              {report.description}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <HStack>
                          <Icon 
                            as={getTypeIcon(report.type)} 
                            color={`${getTypeColor(report.type)}.500`}
                          />
                          <Badge colorScheme={getTypeColor(report.type)}>
                            {report.type.replace('_', ' ')}
                          </Badge>
                        </HStack>
                      </Td>
                      <Td>
                        {report.schedule ? (
                          <HStack>
                            <Icon as={MdSchedule} color="blue.500" />
                            <Text fontSize="sm">{report.schedule.frequency}</Text>
                          </HStack>
                        ) : (
                          <Text fontSize="sm" color="gray.500">Manual</Text>
                        )}
                      </Td>
                      <Td>
                        {report.lastRun ? (
                          <Text fontSize="sm">{formatDate(report.lastRun)}</Text>
                        ) : (
                          <Text fontSize="sm" color="gray.500">Never</Text>
                        )}
                      </Td>
                      <Td>
                        {report.nextRun ? (
                          <Text fontSize="sm">{formatDate(report.nextRun)}</Text>
                        ) : (
                          <Text fontSize="sm" color="gray.500">-</Text>
                        )}
                      </Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(report.status)}>
                          {report.status}
                        </Badge>
                      </Td>
                      <Td>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<MdMoreVert />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<MdPlayArrow />}
                              onClick={() => generateReport(report.id)}
                              isDisabled={generating === report.id}
                            >
                              Generate Now
                            </MenuItem>
                            <MenuItem
                              icon={<MdDownload />}
                              onClick={() => downloadReport(report)}
                            >
                              Download
                            </MenuItem>
                            <MenuItem icon={<MdEdit />}>
                              Edit
                            </MenuItem>
                            <MenuItem icon={<MdSchedule />}>
                              Schedule
                            </MenuItem>
                            <MenuItem 
                              icon={<MdDelete />} 
                              color="red.500"
                              onClick={() => deleteReport(report.id)}
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Create Report Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Report</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Report Name</FormLabel>
                <Input placeholder="Enter report name..." />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input placeholder="Brief description..." />
              </FormControl>

              <FormControl>
                <FormLabel>Report Type</FormLabel>
                <Select>
                  <option value="landlord_activity">Landlord Activity</option>
                  <option value="property_performance">Property Performance</option>
                  <option value="financial">Financial</option>
                  <option value="support_metrics">Support Metrics</option>
                  <option value="custom">Custom</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Schedule</FormLabel>
                <Select>
                  <option value="">Manual (No schedule)</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Data Fields</FormLabel>
                <Stack spacing={2}>
                  <Checkbox defaultChecked>Total Revenue</Checkbox>
                  <Checkbox defaultChecked>Occupancy Rate</Checkbox>
                  <Checkbox>Collection Rate</Checkbox>
                  <Checkbox>Outstanding Balance</Checkbox>
                  <Checkbox>Tenant Count</Checkbox>
                </Stack>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={onCreateClose}>
              Create Report
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

// Mock Data
const mockTemplates: ReportTemplate[] = [
  {
    id: 'T1',
    name: 'Landlord Activity Summary',
    description: 'Overview of landlord engagement and platform usage',
    type: 'landlord_activity',
    dataSource: 'landlords',
    filters: [],
    groupBy: ['month'],
    visualizations: [],
    format: 'pdf',
  },
  {
    id: 'T2',
    name: 'Property Performance',
    description: 'Occupancy rates and revenue by property',
    type: 'property_performance',
    dataSource: 'properties',
    filters: [],
    groupBy: ['property'],
    visualizations: [],
    format: 'excel',
  },
  {
    id: 'T3',
    name: 'Financial Summary',
    description: 'Revenue, collections, and outstanding balances',
    type: 'financial',
    dataSource: 'payments',
    filters: [],
    groupBy: ['month'],
    visualizations: [],
    format: 'pdf',
  },
  {
    id: 'T4',
    name: 'Support Metrics',
    description: 'Ticket volume, response times, and satisfaction',
    type: 'support_metrics',
    dataSource: 'support_tickets',
    filters: [],
    groupBy: ['week'],
    visualizations: [],
    format: 'pdf',
  },
];

const mockReports: Report[] = [
  {
    id: '1',
    name: 'Monthly Financial Report',
    description: 'Comprehensive financial overview for the month',
    type: 'financial',
    template: 'T3',
    parameters: { month: '2025-11' },
    schedule: {
      frequency: 'monthly',
      dayOfMonth: 1,
      time: '08:00',
      timezone: 'America/Toronto',
      enabled: true,
      recipients: ['admin@dossiloc.ca'],
    },
    createdBy: 'admin',
    createdAt: new Date('2025-10-01'),
    lastRun: new Date('2025-11-01'),
    nextRun: new Date('2025-12-01'),
    status: 'active',
  },
  {
    id: '2',
    name: 'Weekly Support Metrics',
    description: 'Support team performance and ticket analytics',
    type: 'support_metrics',
    template: 'T4',
    parameters: {},
    schedule: {
      frequency: 'weekly',
      dayOfWeek: 1,
      time: '09:00',
      timezone: 'America/Toronto',
      enabled: true,
      recipients: ['support@dossiloc.ca'],
    },
    createdBy: 'admin',
    createdAt: new Date('2025-09-15'),
    lastRun: new Date('2025-11-25'),
    nextRun: new Date('2025-12-02'),
    status: 'active',
  },
];
